import { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";
import moment from "moment";
import { useMemo } from "react";
import { ItemsTable } from "ui";

import { AuditLogResponse } from "../../auditLog.types";
import { humanizeAuditAction } from "../../helpers";
import { AdminBadge } from "../AdminBadge";
import { EntityPreview } from "../EntityPreview";
import { AuditLogsTableProps } from "./AuditLogsTable.types";

import styles from "./AuditLogsTable.module.scss";

const AuditLogsTable = ({
  auditLogs,
  isLoading,
  sorting,
  setSorting
}: AuditLogsTableProps) => {
  const columns: Array<ColumnDef<AuditLogResponse, string>> = useMemo(
    () => [
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("Date & time"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => (
          <span className={styles.nowrap}>
            {moment(auditLogs[row.index].createdAt).format("DD/MM/YYYY HH:mm")}
          </span>
        )
      },
      {
        id: "adminName",
        accessorKey: "adminName",
        header: t("Admin"),
        size: 22,
        minSize: 22,
        enableSorting: false,
        cell: ({ row }) => (
          <AdminBadge
            adminId={auditLogs[row.index].adminId}
            adminName={auditLogs[row.index].adminName}
            compact
          />
        )
      },
      {
        id: "action",
        accessorKey: "action",
        header: t("Action"),
        size: 23,
        minSize: 23,
        enableSorting: false,
        cell: ({ row }) => {
          const { action } = auditLogs[row.index];

          return (
            <div className={styles.action}>
              <span className={styles.action__label}>
                {humanizeAuditAction(action)}
              </span>
              <code className={styles.action__raw}>{action}</code>
            </div>
          );
        }
      },
      {
        id: "entity",
        accessorKey: "entityType",
        header: t("Entity"),
        size: 25,
        minSize: 25,
        enableSorting: false,
        cell: ({ row }) => (
          <EntityPreview
            entityType={auditLogs[row.index].entityType}
            entityId={auditLogs[row.index].entityId}
            snapshot={
              auditLogs[row.index].newValue ??
              auditLogs[row.index].previousValue
            }
            compact
          />
        )
      },
      {
        id: "type",
        accessorKey: "id",
        header: t("Type"),
        size: 15,
        minSize: 15,
        enableSorting: false,
        cell: ({ row }) => {
          const isDeep = auditLogs[row.index].entityType !== null;

          return (
            <span
              className={
                isDeep ? styles["badge--deep"] : styles["badge--envelope"]
              }
            >
              {isDeep ? t("Detailed") : t("Request")}
            </span>
          );
        }
      }
    ],
    [auditLogs]
  );

  const deepRowsIndexes = useMemo(
    () =>
      auditLogs.reduce<number[]>((acc, log, index) => {
        if (log.entityType !== null) acc.push(index);
        return acc;
      }, []),
    [auditLogs]
  );

  return (
    <ItemsTable
      columns={columns}
      data={auditLogs}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={64}
      emptyListMessage={t("No activity recorded yet")}
      specificRowsIndexes={deepRowsIndexes}
      specificRowsClassName={styles.deepRow}
      getLinkUrl={({ id }) => `/activity-log/${id}`}
    />
  );
};

export default AuditLogsTable;
